import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { map } from "rxjs";
import { DetailedWeatherConditionsData } from "../../models/weather.models";

interface DetailedPressureComponentState {
    weatherConditions: Record<string, DetailedWeatherConditionsData> | undefined;
    isProcessing: boolean;
    zone: string | undefined;
}

@Component({
    selector: "dtm-map-detailed-pressure",
    templateUrl: "./detailed-pressure.component.html",
    styleUrls: ["./detailed-pressure.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class DetailedPressureComponent {
    @Input()
    public set weatherConditions(value: Record<string, DetailedWeatherConditionsData> | undefined) {
        this.localStore.patchState({ weatherConditions: value });
    }
    @Input()
    public set isProcessing(value: BooleanInput) {
        this.localStore.patchState({ isProcessing: coerceBooleanProperty(value) });
    }
    @Input({ required: true })
    public set zone(value: string | undefined) {
        this.localStore.patchState({ zone: value });
    }

    protected readonly weatherConditions$ = this.localStore.selectByKey("weatherConditions");
    protected readonly meanPressure$ = this.prepareMeanPressureDataStream();
    protected readonly hasStationsData$ = this.weatherConditions$.pipe(
        map((weatherConditions) => !!weatherConditions && Object.keys(weatherConditions).length > 0)
    );
    protected readonly zone$ = this.localStore.selectByKey("zone");
    protected readonly isProcessing$ = this.localStore.selectByKey("isProcessing");

    constructor(private readonly localStore: LocalComponentStore<DetailedPressureComponentState>) {
        this.localStore.setState({
            weatherConditions: undefined,
            isProcessing: false,
            zone: undefined,
        });
    }

    private prepareMeanPressureDataStream() {
        return this.weatherConditions$.pipe(
            map((weatherConditions) => {
                if (!weatherConditions) {
                    return undefined;
                }

                const pressures = Object.values(weatherConditions)
                    .map(
                        ({
                            weatherConditions: {
                                pressure: { meanPressureAtStationLevelHpa },
                            },
                        }) => meanPressureAtStationLevelHpa
                    )
                    .filter((pressure) => pressure !== undefined) as number[];
                const totalPressure = pressures.reduce<number>((result, pressure) => result + pressure, 0);

                if (pressures.length === 0) {
                    return undefined;
                }

                return totalPressure / pressures.length;
            })
        );
    }
}
